import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  ChevronDown, Search, ArrowLeft, 
  User, Shield, Heart, CreditCard, 
  Coins, MessageSquare, Bell, Smartphone,
  HelpCircle, Lock, Calendar, Gift, Sparkles, Wand2
} from "lucide-react";
import Footer from "@/components/Footer";

const FAQPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [openFaq, setOpenFaq] = useState(null);

  const categories = [
    { id: "all", label: "All Questions", icon: HelpCircle },
    { id: "getting-started", label: "Getting Started", icon: User },
    { id: "matching", label: "Matching & Dating", icon: Heart },
    { id: "ai-features", label: "AI Features", icon: Sparkles },
    { id: "subscriptions", label: "Subscriptions & Billing", icon: CreditCard },
    { id: "coins", label: "Coins & Features", icon: Coins },
    { id: "privacy", label: "Privacy & Safety", icon: Shield },
    { id: "technical", label: "Technical Support", icon: Smartphone }
  ];

  const faqs = [
    // Getting Started
    {
      category: "getting-started",
      question: "Who is RecoveryMatch for?",
      answer: "RecoveryMatch is for adults (18+) in recovery from alcohol and substance use who want meaningful romantic connections with people who understand their journey. Whether you're in AA, NA, SMART Recovery, or any other program, you're welcome here."
    },
    {
      category: "getting-started",
      question: "How do I create an account?",
      answer: "Download the app from the App Store, tap 'Sign Up', and follow the steps: enter your email, verify it, complete our compatibility questionnaire, add photos, and you're ready to match! The questionnaire helps us find compatible matches based on your recovery journey and relationship goals."
    },
    {
      category: "getting-started",
      question: "How do I get verified?",
      answer: "Verification helps build trust in our community. Go to Settings > Get Verified and follow the steps to verify your identity. Verified members get a badge on their profile and are prioritized in matching."
    },
    {
      category: "getting-started",
      question: "Can I use RecoveryMatch if I'm new to recovery?",
      answer: "Yes! We welcome people at all stages of their recovery journey. However, we encourage focusing on your recovery first. Many members find it helpful to have at least a few months of sobriety before dating, but there's no minimum requirement."
    },

    // Matching & Dating
    {
      category: "matching",
      question: "How does matching work?",
      answer: "We use a detailed compatibility algorithm based on your recovery journey, values, and relationship goals. You receive curated daily matches (3-15 per day depending on your subscription) with compatibility scores. Express interest, and if it's mutual, you can start chatting!"
    },
    {
      category: "matching",
      question: "Why am I not getting many matches?",
      answer: "Match availability depends on your location, preferences, and compatibility criteria. Try expanding your search radius in Settings, or broadening your preferences. Also make sure your profile is complete with good photos and a thoughtful bio - this increases your match rate significantly!"
    },
    {
      category: "matching",
      question: "What happens when a match expires?",
      answer: "Matches expire after 24 hours if neither person expresses interest. This keeps your feed fresh and encourages timely decisions. Don't worry - if someone is truly compatible, you may see them again in future matches!"
    },
    {
      category: "matching",
      question: "What is a Super Like?",
      answer: "A Super Like is a way to stand out! When you Super Like someone, they're notified immediately and you appear at the top of their matches. Super Likes are available with coins or included in Recovery Elite. Use them on people you're really excited about!"
    },
    {
      category: "matching",
      question: "How does guided messaging work?",
      answer: "To encourage meaningful conversations, we start with guided prompts. You'll exchange 3-5 thoughtful messages based on prompts before unlocking free chat. This helps break the ice and leads to better conversations than 'Hey, what's up?'"
    },

    // AI Features (NEW)
    {
      category: "ai-features",
      question: "What is the AI Bio Assistant?",
      answer: "The AI Bio Assistant helps you write the perfect dating profile bio! Just tell us a bit about yourself - your interests, what makes you unique, what you're looking for - and our AI will craft an engaging, authentic bio that showcases your personality. It's like having a personal copywriter for your profile!"
    },
    {
      category: "ai-features",
      question: "How do AI Conversation Starters work?",
      answer: "Never run out of things to say! When chatting with a match, tap the AI Conversation Starters button to get personalized icebreaker suggestions based on both your profiles. The AI analyzes your shared interests, recovery journeys, and values to suggest meaningful conversation topics - way better than 'Hey, how are you?'"
    },
    {
      category: "ai-features",
      question: "Are the AI features free to use?",
      answer: "AI features have usage limits based on your subscription tier. Free users get 1 AI bio generation and 3 conversation starters per day. Recovery+ members get 3 bio generations and 10 starters daily. Recovery Elite members enjoy unlimited AI bio generations and conversation starters!"
    },
    {
      category: "ai-features",
      question: "Can I edit what the AI writes for my bio?",
      answer: "Absolutely! The AI provides a starting point - you can edit, tweak, or completely rewrite any AI-generated bio before saving it to your profile. We encourage making it your own! You can also generate multiple versions and pick the one that feels most like you."
    },
    {
      category: "ai-features",
      question: "Is the AI personalized to me?",
      answer: "Yes! Our AI considers your profile information, recovery journey, interests, and relationship goals to generate personalized content. For conversation starters, it also looks at your match's profile to find common ground and suggest relevant topics you both might enjoy discussing."
    },
    {
      category: "ai-features",
      question: "Will people know if I used AI to write my bio?",
      answer: "Nope! There's no indicator that shows whether you used AI assistance. The AI is just a tool to help you express yourself better - the thoughts and personality behind the bio are still authentically yours. Think of it like using spell-check, just more creative!"
    },

    // Subscriptions & Billing
    {
      category: "subscriptions",
      question: "Is RecoveryMatch free?",
      answer: "Yes! Basic features are free with 3 matches per day. Recovery+ ($9.99/mo) gives you 10 daily matches and lets you see who liked you. Recovery Elite ($19.99/mo) includes 15 matches, Super Likes, video calls, advanced filters, and profile boosts."
    },
    {
      category: "subscriptions",
      question: "What's the difference between subscription tiers?",
      answer: "Free: 3 daily matches, guided messaging, basic filters. Recovery+ ($9.99/mo): 10 daily matches, see who likes you, unlimited messaging. Recovery Elite ($19.99/mo): 15 daily matches, Super Likes, video calls, profile boosts, advanced filters, read receipts, and priority support."
    },
    {
      category: "subscriptions",
      question: "How do I cancel my subscription?",
      answer: "You can cancel anytime through your device's subscription settings. On iPhone: Settings > Apple ID > Subscriptions > RecoveryMatch > Cancel. Your premium features remain active until the end of your billing period."
    },
    {
      category: "subscriptions",
      question: "Can I get a refund for my subscription?",
      answer: "We offer refunds within 14 days of purchase if you haven't extensively used premium features. Contact us at support@recoverymatch.com with your account email. Refunds are processed through Apple/Google based on their policies."
    },
    {
      category: "subscriptions",
      question: "What happens when my subscription expires?",
      answer: "You'll be notified before your subscription expires (7 days, 3 days, and 1 day before). When it expires, you'll return to the free tier but keep all your matches, conversations, and profile. You can resubscribe anytime to restore premium features."
    },

    // Coins & Features
    {
      category: "coins",
      question: "What are coins and what can I use them for?",
      answer: "Coins are our in-app currency for special features. Use coins for: Super Likes (stand out to someone special), Profile Boosts (appear at the top of matches for 30 minutes), and Gifts (send virtual gifts to show interest). You can buy coins in the Gift Shop or earn them through referrals."
    },
    {
      category: "coins",
      question: "How do I buy coins?",
      answer: "Go to the Gift Shop in the app. We offer three coin packs: 100 coins ($1.99), 550 coins ($7.99), and 1,400 coins ($14.99). Larger packs give you bonus coins! Purchases are processed securely through the App Store."
    },
    {
      category: "coins",
      question: "What is a Profile Boost?",
      answer: "A Profile Boost puts your profile at the top of other users' daily matches for 30 minutes. This significantly increases your visibility and match rate. Boosts cost coins and work best during peak hours (evenings and weekends)."
    },
    {
      category: "coins",
      question: "How do I send a gift?",
      answer: "On any profile or in a conversation, tap the gift icon to browse available gifts. Select a gift, add an optional message, and send! The recipient will be notified and can see your gift on their profile. Gifts are a great way to break the ice!"
    },
    {
      category: "coins",
      question: "How does the referral program work?",
      answer: "Share your unique referral code with friends! When someone signs up using your code and completes their profile, you both get rewards: 200 coins and a free month of Recovery+. Find your code in Settings > Refer a Friend."
    },

    // Privacy & Safety
    {
      category: "privacy",
      question: "Is my recovery information private?",
      answer: "Yes! You control what you share. Your sobriety date and program details are only visible to other members, and you can adjust privacy settings anytime. We never share your information with third parties, and your profile won't appear in search engines."
    },
    {
      category: "privacy",
      question: "How do I block or report someone?",
      answer: "On any profile, tap the three dots (...) menu and select 'Block' or 'Report'. Blocking immediately hides you from each other. Reports are reviewed by our team within 24 hours. We take safety seriously - inappropriate behavior results in account removal."
    },
    {
      category: "privacy",
      question: "What is Date Check-in?",
      answer: "Date Check-in is our safety feature for when you meet someone in person. Before your date, set a check-in time and emergency contact. At check-in time, you'll get a notification to confirm you're safe. If you don't respond or indicate you need help, your emergency contact is automatically notified."
    },
    {
      category: "privacy",
      question: "How do I delete my account?",
      answer: "Go to Settings > Account > Delete Account in the app. You'll need to confirm your decision. Your data will be permanently removed within 30 days. Can't access the app? Visit recoverymatch.com/delete-account to request deletion online, or email support@recoverymatch.com. Note: deletion is permanent and cannot be undone."
    },
    {
      category: "privacy",
      question: "Can people from my meetings see me on RecoveryMatch?",
      answer: "It's possible! If you prefer, you can use only your first name and be selective about your photos. Remember, anyone on RecoveryMatch is also in recovery, so you're in a community of people who understand. You can always block someone if needed."
    },

    // Technical Support
    {
      category: "technical",
      question: "Why aren't my push notifications working?",
      answer: "Check your phone's Settings > Notifications > RecoveryMatch and make sure notifications are enabled. Also check in-app: Settings > Notification Settings. If still not working, try logging out and back in. On iPhone, also check Focus/Do Not Disturb settings."
    },
    {
      category: "technical",
      question: "My photos won't upload. What should I do?",
      answer: "Make sure you have a stable internet connection and have granted camera/photo permissions. Photos must be under 10MB. Try closing and reopening the app. If issues persist, try uploading from a different album or taking a new photo."
    },
    {
      category: "technical",
      question: "The app is crashing or freezing. Help!",
      answer: "First, try force-closing the app and reopening it. If that doesn't work: 1) Check for app updates in the App Store, 2) Restart your phone, 3) Delete and reinstall the app (your account and data are safe). Contact support if issues continue."
    },
    {
      category: "technical",
      question: "I'm not receiving the verification email.",
      answer: "Check your spam/junk folder first - sometimes our emails land there. If not found, tap 'Resend' on the verification screen. Make sure you entered your email correctly. Try adding support@recoverymatch.com to your contacts. Gmail users should check the Promotions tab."
    },
    {
      category: "technical",
      question: "How do I change my email or phone number?",
      answer: "Go to Settings > Account > Email/Phone. You'll need to verify your new email or phone number before the change takes effect. If you no longer have access to your current email, contact support@recoverymatch.com for help."
    }
  ];

  // Filter FAQs based on search and category
  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = searchQuery === "" || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === "all" || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-amber-50">
      {/* Header - Transparent Style */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/images/logo-main.png" 
              alt="RecoveryMatch" 
              className="w-10 h-10 rounded-xl"
            />
            <span className="font-bold text-xl">
              <span className="text-[#1C3A7A]">Recovery</span>
              <span className="text-[#FAB710]">Match</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <button onClick={() => navigate('/')} className="hover:text-[#1C3A7A] transition-colors">Features</button>
            <button onClick={() => navigate('/founder')} className="hover:text-[#1C3A7A] transition-colors">About</button>
            <button onClick={() => navigate('/faq')} className="text-[#1C3A7A] font-semibold transition-colors">FAQ</button>
            <button onClick={() => navigate('/contact')} className="hover:text-[#1C3A7A] transition-colors">Contact</button>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/login')}
              className="text-[#1C3A7A] hover:text-[#FAB710] font-medium transition px-4 py-2 hidden sm:block"
            >
              Log In
            </button>
            <button 
              onClick={() => navigate('/register')}
              className="bg-[#FAB710] text-white px-5 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-orange-200 hover:scale-105 transition-all duration-300"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Header Section */}
      <div className="pt-28 pb-12 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1C3A7A] mb-4">How can we help?</h1>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Find answers to common questions about RecoveryMatch. Can't find what you're looking for? 
            <Link to="/contact" className="text-[#FAB710] hover:underline ml-1 font-medium">Contact us</Link>
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FAB710] shadow-lg border border-gray-100 bg-white"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 pb-12">
        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === category.id
                  ? 'bg-[#1C3A7A] text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <category.icon className="w-4 h-4" />
              {category.label}
            </button>
          ))}
        </div>

        {/* Results Count */}
        {searchQuery && (
          <p className="text-gray-500 text-sm mb-4 text-center">
            Found {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''} for "{searchQuery}"
          </p>
        )}

        {/* FAQ List */}
        <div className="space-y-3">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition"
                >
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-lg ${
                      faq.category === 'getting-started' ? 'bg-blue-100 text-blue-600' :
                      faq.category === 'matching' ? 'bg-pink-100 text-pink-600' :
                      faq.category === 'ai-features' ? 'bg-purple-100 text-purple-600' :
                      faq.category === 'subscriptions' ? 'bg-green-100 text-green-600' :
                      faq.category === 'coins' ? 'bg-yellow-100 text-yellow-600' :
                      faq.category === 'privacy' ? 'bg-indigo-100 text-indigo-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {faq.category === 'getting-started' ? <User className="w-4 h-4" /> :
                       faq.category === 'matching' ? <Heart className="w-4 h-4" /> :
                       faq.category === 'ai-features' ? <Sparkles className="w-4 h-4" /> :
                       faq.category === 'subscriptions' ? <CreditCard className="w-4 h-4" /> :
                       faq.category === 'coins' ? <Coins className="w-4 h-4" /> :
                       faq.category === 'privacy' ? <Shield className="w-4 h-4" /> :
                       <Smartphone className="w-4 h-4" />}
                    </div>
                    <span className="font-medium text-[#1C3A7A] pr-4">{faq.question}</span>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${
                    openFaq === index ? 'rotate-180' : ''
                  }`} />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 pt-0">
                    <div className="pl-11 text-gray-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
              <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No questions found matching your search.</p>
              <button 
                onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
                className="mt-4 text-[#1C3A7A] hover:text-[#FAB710] font-medium"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>

        {/* Still Need Help */}
        <div className="mt-12 bg-gradient-to-r from-[#1C3A7A] to-[#2d4a8a] rounded-2xl p-8 text-white text-center shadow-xl">
          <h3 className="text-2xl font-bold mb-2">Still have questions?</h3>
          <p className="text-white/80 mb-6">Our support team is here to help you on your journey.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/contact')}
              className="px-6 py-3 bg-[#FAB710] text-[#1C3A7A] font-semibold rounded-xl hover:bg-[#f5c542] transition"
            >
              Contact Support
            </button>
            <a 
              href="mailto:support@recoverymatch.com"
              className="px-6 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition"
            >
              Email Us
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          <Link to="/privacy" className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition flex items-center gap-3">
            <Lock className="w-8 h-8 text-[#1C3A7A]" />
            <div>
              <p className="font-medium text-[#1C3A7A]">Privacy Policy</p>
              <p className="text-sm text-gray-500">How we protect your data</p>
            </div>
          </Link>
          <Link to="/terms" className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#1C3A7A]" />
            <div>
              <p className="font-medium text-[#1C3A7A]">Terms of Service</p>
              <p className="text-sm text-gray-500">Our usage guidelines</p>
            </div>
          </Link>
          <Link to="/safety" className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition flex items-center gap-3">
            <Heart className="w-8 h-8 text-[#1C3A7A]" />
            <div>
              <p className="font-medium text-[#1C3A7A]">Community Guidelines</p>
              <p className="text-sm text-gray-500">How we keep you safe</p>
            </div>
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQPage;
